"use client"

import { useState } from "react"
import {
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  Flame,
  Target,
  TrendingUp,
  Calendar,
  ActivityIcon,
} from "lucide-react"

interface Subject {
  name: string
  ca: number
  exam: number
  total: number
  grade: string
  color: string
}

interface Task {
  id: string
  title: string
  subject: string
  status: "completed" | "pending" | "overdue"
}

interface StudentActivity {
  id: string
  action: string
  timestamp: string
  category: string
}

interface PerformanceTabsProps {
  subjects: Subject[]
  tasks: Task[]
  activities: StudentActivity[]
  tasksCompleted: number
  currentStreak: number
  engagementRate: number
  attendanceRate: number
}

export function PerformanceTabs({
  subjects,
  tasks,
  activities,
  tasksCompleted,
  currentStreak,
  engagementRate,
  attendanceRate,
}: PerformanceTabsProps) {
  const [activeTab, setActiveTab] = useState<"academic" | "student">("academic")

  const completedTasks = tasks.filter((t) => t.status === "completed").length
  const pendingTasks = tasks.filter((t) => t.status === "pending").length
  const overdueTasks = tasks.filter((t) => t.status === "overdue").length

  const gradeColors: Record<string, { bg: string; text: string }> = {
    "A+": { bg: "bg-teal-50", text: "text-teal-600" },
    A: { bg: "bg-teal-50", text: "text-teal-600" },
    B: { bg: "bg-blue-50", text: "text-blue-600" },
    C: { bg: "bg-yellow-50", text: "text-yellow-600" },
    D: { bg: "bg-orange-50", text: "text-orange-600" },
    F: { bg: "bg-red-50", text: "text-red-600" },
  }

  const barColors: Record<string, string> = {
    teal: "bg-teal-500",
    blue: "bg-blue-500",
    orange: "bg-orange-500",
    purple: "bg-purple-500",
    yellow: "bg-yellow-500",
    gray: "bg-gray-500",
    pink: "bg-pink-500",
    indigo: "bg-indigo-500",
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 flex flex-col lg:col-span-8 min-h-[400px]">
      <div className="flex items-center justify-between border-b border-gray-200 px-5">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab("academic")}
            className={`py-4 border-b-2 ${
              activeTab === "academic"
                ? "border-teal-500 text-gray-900 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-900 font-medium"
            } text-sm transition-colors`}
          >
            Academic Performance
          </button>
          <button
            onClick={() => setActiveTab("student")}
            className={`py-4 border-b-2 ${
              activeTab === "student"
                ? "border-teal-500 text-gray-900 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-900 font-medium"
            } text-sm transition-colors`}
          >
            Student Performance
          </button>
        </div>
        <button className="text-xs font-bold text-teal-500 flex items-center gap-1 hover:text-teal-600">
          <Download className="w-3.5 h-3.5" /> Download Report
        </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col p-0">
        {activeTab === "academic" ? (
          <div className="overflow-x-auto h-full">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="text-xs text-gray-500 font-bold uppercase bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-5 py-3 w-1/3">Subject</th>
                  <th className="px-5 py-3 text-center">Cont. Assess. (40)</th>
                  <th className="px-5 py-3 text-center">Exam (60)</th>
                  <th className="px-5 py-3 text-center">Total (100)</th>
                  <th className="px-5 py-3 text-center">Grade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-900">
                {subjects.map((subject) => (
                  <tr key={subject.name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 font-medium flex items-center gap-2">
                      <span className={`w-1 h-6 rounded-full ${barColors[subject.color]}`}></span>
                      {subject.name}
                    </td>
                    <td className="px-5 py-3 text-center text-gray-500">{subject.ca}</td>
                    <td className="px-5 py-3 text-center text-gray-500">{subject.exam}</td>
                    <td className="px-5 py-3 text-center font-bold">{subject.total}</td>
                    <td className="px-5 py-3 text-center">
                      <span
                        className={`font-bold ${gradeColors[subject.grade].bg} ${gradeColors[subject.grade].text} rounded-lg text-xs px-2 py-1 inline-block`}
                      >
                        {subject.grade}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-500 text-white flex items-center justify-center">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{tasksCompleted}</p>
                    <p className="text-xs text-gray-600">Tasks Completed</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center">
                    <Flame className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{currentStreak}</p>
                    <p className="text-xs text-gray-600">Day Streak</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 text-white flex items-center justify-center">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{engagementRate}%</p>
                    <p className="text-xs text-gray-600">Engagement Rate</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500 text-white flex items-center justify-center">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{attendanceRate}%</p>
                    <p className="text-xs text-gray-600">Attendance</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-teal-500" />
                Tasks Overview
              </h3>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{completedTasks}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{pendingTasks}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{overdueTasks}</p>
                  <p className="text-xs text-gray-500">Overdue</p>
                </div>
              </div>

              <div className="space-y-2">
                {tasks.slice(0, 5).map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {task.status === "completed" ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : task.status === "overdue" ? (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      ) : (
                        <Clock className="w-4 h-4 text-blue-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{task.title}</p>
                        <p className="text-xs text-gray-500">{task.subject}</p>
                      </div>
                    </div>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded ${
                        task.status === "completed"
                          ? "bg-emerald-100 text-emerald-700"
                          : task.status === "overdue"
                            ? "bg-red-100 text-red-700"
                            : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {task.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <ActivityIcon className="w-5 h-5 text-teal-500" />
                Recent Activities on EarlyScape
              </h3>

              <div className="space-y-3">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-2"></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.action}</p>
                      <p className="text-xs text-gray-500">{activity.timestamp}</p>
                    </div>
                    <span className="text-xs text-gray-400 capitalize">{activity.category}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
